import { DataTable, Icon } from '@ampol/design-system';
import React, { useEffect, useState, useRef } from 'react';

import './account-address-list.scss';
import { addressTypeTitle } from 'utilities/constants';
import AddressForm from 'forms/address/address-form';
import { getAddressesByContract, updateAddressesByContract, updateAddressesReset } from 'actions/accountActions';
import { useDispatch, useSelector } from 'react-redux';
import { setAccountAddressEditButtons, setAccountDefaultButton } from 'actions/modalMetaActions';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Describe component here
 * @param props
 */
const AccountAddressList = ({ contractNumber }) => {
  const dispatch = useDispatch();

  const formId = 'address-edit';

  const [selectedAddress, setSelectedAddress] = useState(null);

  const canReadWrite = useSelector((state) => state.userStore.canReadWriteAccounts);
  const addressDetails = useSelector((state) => state.accountsStore.addressDetails);
  const isAddressLoading = useSelector((state) => state.accountsStore.isAddressLoading);
  const isAddressUpdateLoading = useSelector((state) => state.accountsStore.isAddressUpdateLoading);
  const isAddressUpdateLoaded = useSelector((state) => state.accountsStore.isAddressUpdateLoaded);

  const assignedGrandfatherClientNumber = useSelector((state) => state.userStore.assignedGrandfatherClientNumber);
  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);

  const isMounted = useRef(false);

  const addressColumns = [
    {
      header: 'Address Type',
      accessorFn: (address) => addressTypeTitle[address.addressTypeCode] || address.addressType.split(';')?.[1],
    },
    {
      header: 'Address',
      accessorKey: 'addressShortName',
    },
    {
      id: 'action',
      accessorFn: () => <Icon url={'/icons/edit.svg'}/>,
      rtl: true,
    },
  ];

  const handleAddressUpdate = (values) => {
    const cleanedEmailList = values?.email ? values?.email.split(';').filter(email => email.trim() !== '').join(';') : undefined;

    const payload = {
      contractSearchMethod: 'CONTRACT_NUMBER',
      contractIdentifier: contractNumber,
      addressTypeCode: values.addressTypeCode,
      addressId: selectedAddress.addressId,
      reason: 'Customer update request',
      deliveryTypeCode: values.deliveryTypeCode,
      salutationCode: values.salutationCode,
      contractAddressDetails: {
        country: 'AUS',
        ...values,
        line1: values?.line1?.trim(),
        line2: values?.line2?.trim(),
        city: values?.city?.trim(),
        businessPhone: values?.businessPhone?.trim(),
        mobilePhone: values?.mobilePhone?.trim(),
        businessFax: values?.businessFax?.trim(),
        email: cleanedEmailList,
      },
      grandParentAccountNumber: assignedGrandfatherClientNumber,
    };

    dispatch(updateAddressesByContract(payload));
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Address_Update_Submit',
      selected: true
    });
    trackCustomEvent('Submit Update Address', loggedInUser, assignedAccountNumber, 'Clicked Update button to submit Address Update.', { selectedAccount: contractNumber });

    closeAddressEdit();
  };

  const handleAddressStateChange = ({ isValid }) => {
    updateEditButtons(!isValid);
  };

  const closeAddressEdit = () => {
    setSelectedAddress(null);

    dispatch(setAccountDefaultButton());
  };

  const closeAddressEditFunc = () => {
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Address_Update_Cancel',
      button_click: true,
    });
    closeAddressEdit();
  };

  const handleAddressRowClick = (address) => {
    const addressType = address.clientAddressTypeCode.split(';')[1];
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Address_Update_Select',
      button_click: true,
      address_type: addressType
    });
    trackCustomEvent('Click Edit Address', loggedInUser, assignedAccountNumber, 'Click on Edit button of an address.', { selectedAccount: contractNumber });
    setSelectedAddress(address);

    updateEditButtons();
  };

  const updateEditButtons = (inactive) => {
    dispatch(setAccountAddressEditButtons({
      formId: formId,
      onCancelClick: closeAddressEditFunc,
      inactive: inactive,
    }));
  };

  useEffect(() => {
    dispatch(updateAddressesReset());
    dispatch(getAddressesByContract(contractNumber, assignedGrandfatherClientNumber));

    return () => dispatch(setAccountDefaultButton());
  }, []);

  useEffect(() => {
    if (isAddressUpdateLoaded && isMounted.current) {
      dispatch(getAddressesByContract(contractNumber, assignedGrandfatherClientNumber));
      dispatch(updateAddressesReset());
    }
    isMounted.current = true;
  }, [isAddressUpdateLoaded]);

  return <div className={'account-address-list'}>
    {!selectedAddress && <DataTable
      columns={addressColumns}
      caption={'Addresses'}
      data={addressDetails}
      isSortable={true}
      tableVariant={'list'}
      isLoading={isAddressLoading || isAddressUpdateLoading}
      loadingRowCount={3}
      onRowClick={handleAddressRowClick}
      columnTemplate="1fr 2fr 70px"
    />}
    {selectedAddress &&
      <AddressForm
        formId={formId}
        title={selectedAddress.addressType?.split(';')?.[1]}
        addressType={selectedAddress.addressTypeCode}
        address={selectedAddress}
        onSubmit={handleAddressUpdate}
        onStateChange={handleAddressStateChange}
        readOnly={!canReadWrite}
      />}
  </div>;
};

export default AccountAddressList;
